import type { OperationCapability } from '@common/transport/operation-policy';

/**
 * Office aliases from r2-google-addin/docs/endpoint-matrix.md (E02–E17). Method is part of the
 * operation identity: POST pagination is safe, whereas file checks may finalize
 * work and remain writes until the upstream contract establishes otherwise.
 * Keep HTTPS exceptions explicit; an encryption failure cannot select them.
 */
export function officeTransportCapabilities(secureEnabled: boolean): Readonly<Record<string, OperationCapability>>
{
    const transport = secureEnabled ? 'secure' : 'https';
    const read: OperationCapability = { transport, semantics: 'safe-read' };
    const write: OperationCapability = { transport, semantics: 'write' };
    const httpsRead: OperationCapability = { transport: 'https', semantics: 'safe-read' };
    const httpsWrite: OperationCapability = { transport: 'https', semantics: 'write' };

    return {
        'POST /msg/send': write,
        'POST /msgs/get': read,
        'POST /thread/create-if-not-exists': write,
        'POST /thread/id/assign': write,
        'POST /thread/delete': write,
        'POST /thread/msg/delete': write,
        'POST /group-chat/user/msg/add': write,
        'POST /group-chat/user/msgs/get': read,
        'POST /group-chat/user/msg/get': read,
        'POST /group-chat/users/get': read,
        'POST /group-chat/user/msg/delete': write,
        'POST /group-chat/user/msgs/delete/all': write,
        // Existing Office audio contract only; no new recording/transcription flow.
        'POST /audio/transcribe': write,
        'GET /file/limits/get': read,
        'POST /file/upload/start': write,
        'POST /file/upload/check': write,
        'POST /file/download/start': write,
        'POST /file/download/check': write,
        'GET /copilots': read,
        'POST /subscription/user-subscription': read,
        'POST /sellcode/apply': write,

        'POST /skills/unwrap': httpsWrite,
        'POST /v3/skills/unwrap': httpsRead,
        'POST /contacts/support': httpsWrite,
        'POST /contacts/report-issue': httpsWrite,
        'GET /models': httpsRead,
        'GET /user/settings': httpsRead,
        'GET /skills': httpsRead,
        'GET /skill-tags': httpsRead,
        'GET /instructions': httpsRead,
        'GET /locales': httpsRead,
        'GET /message-history-presets': httpsRead,
        'GET /answer-lengths': httpsRead,
        'GET /chat-defaults': httpsRead,
    };
}
